import { SatelliteData } from "../earth-globe-r3f";

// Constantes del componente original
export const EARTH_RADIUS = 10; // Radio visual en unidades de Three.js
export const EARTH_RADIUS_KM = 6371; // Radio real de la Tierra en km

// Radios orbitales visuales para mostrar (se ciclan para múltiples satélites)
export const VISUAL_ORBIT_RADII = [
  EARTH_RADIUS + 1.2, // Órbita más cercana
  EARTH_RADIUS + 1.5, // Órbita intermedia
  EARTH_RADIUS + 1.8, // Órbita más lejana
];

// Altitudes por defecto para satélites demo (km)
export const DEFAULT_ALTITUDES = [400, 800, 20200]; // Altitudes típicas LEO, Polar, MEO

// Velocidades por defecto para satélites demo (km/h) - velocidades aproximadas a esas altitudes
export const DEFAULT_SPEEDS = [27600, 25200, 14000]; // Velocidades típicas LEO, Polar, MEO

/**
 * Interfaz de configuración de satélite
 */
export interface SatelliteConfig {
  name: string;
  radius: number;
  inclination: number;
  heading: number;
  color: string | number;
  initialAngle: number;
  speed: number;
}

/**
 * Calcular velocidad angular basada en el período orbital real.
 * La velocidad real y altitud del satélite determinan cuánto tarda en completar una órbita.
 * Usamos esto para asegurar que la animación visual coincida con el tiempo orbital real.
 *
 * @param speedKmH - Velocidad del satélite en km/h
 * @param altitudeKm - Altitud orbital sobre la superficie terrestre en km
 * @returns Velocidad angular en radianes/ms
 */
export function calculateAngularSpeed(
  speedKmH: number,
  altitudeKm: number
): number {
  // Radio orbital real en km
  const realOrbitalRadiusKm = EARTH_RADIUS_KM + altitudeKm;
  // Circunferencia orbital real en km
  const circumferenceKm = 2 * Math.PI * realOrbitalRadiusKm;
  // Convertir velocidad a km/ms
  const speedKmMs = speedKmH / (3600 * 1000);
  // Período orbital en ms (tiempo para completar una órbita completa)
  const orbitalPeriodMs = circumferenceKm / speedKmMs;
  // Velocidad angular en radianes/ms (2π radianes por período orbital)
  return (2 * Math.PI) / orbitalPeriodMs;
}

// Paleta de colores para satélites (por posición en el array)
const SATELLITE_COLORS = [
  "#00ffff",
  "#ff6600",
  "#ff00ff",
  "#00ff88",
  "#ffdd00",
  "#7b61ff",
  "#ff4466",
  "#00aaff",
];

/**
 * Obtener color basado en el tipo de satélite
 *
 * @param type - Tipo de satélite
 * @returns Número de color hexadecimal
 */
// Obtener color del satélite según su posición en el array
export function getSatelliteColor(index: number) {
  return SATELLITE_COLORS[index % SATELLITE_COLORS.length];
}

/**
 * Convertir lat/lon a ángulo inicial en el plano orbital.
 * Esto posiciona el satélite basándose en sus coordenadas reales.
 * En lon=0 (Greenwich), el satélite debería estar en ángulo = -initialRotation
 * porque la Tierra ha rotado initialRotation desde la posición por defecto.
 *
 * @param lon - Longitud en grados
 * @param initialRotation - Rotación inicial de la Tierra en radianes
 * @returns Ángulo inicial en radianes
 */
export function latLonToInitialAngle(
  lon: number,
  initialRotation: number
): number {
  // Convertir longitud a radianes
  const lonRad = (lon * Math.PI) / 180;
  // El ángulo del satélite debe compensar la rotación inicial de la Tierra
  // para que aparezca sobre la longitud correcta en el globo
  return lonRad - initialRotation;
}

/**
 * Construir configuración de satélite a partir de datos de satélite.
 * Encapsula el cálculo de configuración por satélite incluyendo:
 * - Asignación de radio orbital visual (ciclando entre VISUAL_ORBIT_RADII)
 * - Normalización de rumbo y detección de órbita retrógrada
 * - Inclinación desde trigonometría esférica (cos(i) = sin(rumbo) * cos(latitud))
 * - Cálculo de ángulo orbital con ajuste norte/sur
 * - Rotación Y para ubicación correcta de longitud
 * - Velocidad angular con inversión de signo para retrógrada
 *
 * @param sat - Datos del satélite
 * @param index - Índice del satélite para asignación de radio orbital
 * @param initialRotation - Rotación inicial de la Tierra en radianes
 * @returns Configuración del satélite
 */
export function buildSatelliteConfig(
  sat: SatelliteData,
  index: number,
  initialRotation: number
): SatelliteConfig {
  const radius = VISUAL_ORBIT_RADII[index % VISUAL_ORBIT_RADII.length];

  const headingRad = (sat.heading * Math.PI) / 180;
  const latRad = (sat.currentPosition.latitude * Math.PI) / 180;
  const lonRad = (sat.currentPosition.longitude * Math.PI) / 180;

  // === Normalizar rumbo: h y h+180° son la misma órbita, diferente dirección ===
  let effectiveHeadingRad = headingRad;
  let isRetrograde = false;
  if (effectiveHeadingRad >= Math.PI) {
    effectiveHeadingRad -= Math.PI;
    isRetrograde = true;
  }

  // === PASO 1: Inclinación desde trigonometría esférica ===
  // cos(i) = sin(rumbo) * cos(latitud)
  const cosI = Math.sin(effectiveHeadingRad) * Math.cos(latRad);
  let inclination = Math.acos(Math.max(-1, Math.min(1, Math.abs(cosI))));
  // La órbita debe poder alcanzar la latitud del satélite
  inclination = Math.max(inclination, Math.abs(latRad));

  // === PASO 2: Argumento de latitud (ángulo orbital) ===
  let orbitalAngle = 0;
  if (inclination > 0.001) {
    const sinTheta = -Math.sin(latRad) / Math.sin(inclination);
    orbitalAngle = Math.asin(Math.max(-1, Math.min(1, sinTheta)));
  }

  // effectiveHeading ∈ [0, π): [0, π/2) → yendo al norte; [π/2, π) → yendo al sur
  const goingNorth = effectiveHeadingRad < Math.PI / 2;
  if (!goingNorth) {
    orbitalAngle = Math.PI - orbitalAngle;
  }

  // === PASO 3: Rotación Y para ubicar la órbita en la longitud correcta ===
  const targetWorldAngle = -(lonRad + initialRotation);

  const localXZAngle = Math.atan2(
    Math.sin(orbitalAngle) * Math.cos(inclination),
    Math.cos(orbitalAngle)
  );
  const yRotation = localXZAngle - targetWorldAngle;

  // === PASO 4: Velocidad — el signo depende de la dirección norte/sur Y si es retrógrada ===
  // Ir al norte requiere ángulo decreciente (speed negativo); isRetrograde invierte eso.
  // Si ambos son true o ambos son false, el speed es positivo; si difieren, es negativo.
  const angularSpeed = calculateAngularSpeed(sat.speed, sat.altitude);

  return {
    name: sat.id,
    radius,
    inclination,
    heading: yRotation,
    color: getSatelliteColor(index),
    initialAngle: orbitalAngle,
    speed: isRetrograde === goingNorth ? angularSpeed : -angularSpeed,
  };
}

/**
 * Construir configuraciones de satélites a partir de un array de datos.
 * Si no se proporcionan datos, retorna configuraciones por defecto (LEO, Polar, MEO).
 *
 * @param satelliteData - Array de datos de satélites (opcional)
 * @param initialRotation - Rotación inicial de la Tierra en radianes
 * @returns Array de configuraciones de satélites
 */
export function buildSatelliteConfigs(
  satelliteData: SatelliteData[] | undefined,
  initialRotation: number
): SatelliteConfig[] {
  if (satelliteData?.length) {
    return satelliteData.map((sat, index) =>
      buildSatelliteConfig(sat, index, initialRotation)
    );
  }

  // Configuraciones por defecto de respaldo
  return [
    {
      name: "LEO",
      radius: EARTH_RADIUS + 0.6,
      inclination: Math.PI / 9,
      heading: 0,
      color: 0x00ffff,
      initialAngle: 0,
      speed: calculateAngularSpeed(DEFAULT_SPEEDS[0], DEFAULT_ALTITUDES[0]),
    },
    {
      name: "Polar",
      radius: EARTH_RADIUS + 1.2,
      inclination: Math.PI / 2,
      heading: 0,
      color: 0xff6600,
      initialAngle: Math.PI / 3,
      speed: calculateAngularSpeed(DEFAULT_SPEEDS[1], DEFAULT_ALTITUDES[1]),
    },
    {
      name: "MEO",
      radius: EARTH_RADIUS + 3,
      inclination: Math.PI / 3.3,
      heading: 0,
      color: 0xff00ff,
      initialAngle: (2 * Math.PI) / 3,
      speed: calculateAngularSpeed(DEFAULT_SPEEDS[2], DEFAULT_ALTITUDES[2]),
    },
  ];
}

/**
 * Crear puntos de geometría de trayectoria orbital.
 * Retorna un Float32Array adecuado para BufferAttribute (no un array de Vector3).
 *
 * @param radius - Radio orbital
 * @param segments - Número de segmentos en la trayectoria orbital (por defecto 128)
 * @returns Float32Array de puntos de la trayectoria orbital
 */
export function createOrbitPoints(
  radius: number,
  segments: number = 128
): Float32Array {
  const points = new Float32Array(segments * 3);
  for (let i = 0; i < segments; i++) {
    const angle = (i / segments) * Math.PI * 2;
    points[i * 3] = Math.cos(angle) * radius;
    points[i * 3 + 1] = 0;
    points[i * 3 + 2] = Math.sin(angle) * radius;
  }
  return points;
}
